import { along } from '@turf/along';
import { feature } from '@turf/helpers';
import { length } from '@turf/length';
import { addDays, isAfter, parseISO } from 'date-fns';
import type { Feature, LineString, Point } from 'geojson';
import { parse } from 'wellknown';
import type { TrafikverketSourceOptions } from './Sources.types';
import { TrafikverketSource } from './TrafikverketSource';

const defaultOptions = (region: string): TrafikverketSourceOptions => ({
    id: 'road-condition-message-source',
    objectType: 'RoadCondition',
    showErrorToast: false,
    schemaVersion: '1.2',
    query: `
        <FILTER>
            <AND>
                <IN name="CountyNo" value="${region === 'vst' ? '1' : '13,14'}" />
                <GT name="ConditionCode" value="1" />
            </AND>
        </FILTER>
        <EXCLUDE>Geometry.SWEREF99TM</EXCLUDE>
    `,
});

export interface TrafikverketRoadCondition {
    id: string;
    geometry: {
        WGS84: string;
    };
    cause: string[];
    conditionCode: number;
    conditionInfo: string[];
    conditionText: string;
    locationText: string;
    measurement: string[];
    startTime: string;
    warning: string[];
}

export interface RoadConditionMessageProperties extends TrafikverketRoadCondition {
    iconId: number;
}

export class RoadConditionMessageSource extends TrafikverketSource {
    constructor(region: string, options?: Partial<TrafikverketSourceOptions>) {
        super({ ...defaultOptions(region), ...options });
    }

    parseData(roadConditions: TrafikverketRoadCondition[]): Feature<Point, RoadConditionMessageProperties>[] {
        return roadConditions
            .filter((rc) => rc.conditionCode > 1 && rc.startTime != null && isAfter(parseISO(rc.startTime), addDays(new Date(), -1)))
            .map((rc) => {
                const lineString = rc.geometry && rc.geometry.WGS84 && (parse(rc.geometry.WGS84) as LineString);
                if (!lineString) {
                    return null;
                }
                const geoJSON = feature(lineString);
                const sectionLength = length(geoJSON);
                const midPoint = along(geoJSON, sectionLength / 2);
                return {
                    id: rc.id,
                    type: 'Feature',
                    geometry: midPoint.geometry,
                    properties: {
                        ...rc,
                        iconId: 107,
                    },
                };
            });
    }
}
